import React from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  Icon,
} from "@salestrak/ui";
import type { EntityData } from "#app/lib/entity-visuals";
import { EntityTag } from "./entity-tag";
import type { Omittable } from "@salestrak/ui";

// One Dialog on every viewport: `bottomStickOnMobile` (the popup default)
// gives the mobile presentation, and the header prop is THE header everywhere.
export type EntityDetailShellProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  header: React.ReactNode;
  srDescription: string;
  body: React.ReactNode;
  footer?: React.ReactNode;
  // When provided, renders a footer button that runs onEdit() then closes the
  // dialog. Supersedes footer.
  onEdit?: () => void;
  editLabel?: string;
}>;

export function EntityDetailShell({
  open,
  onOpenChange,
  header,
  srDescription,
  body,
  footer,
  onEdit,
  editLabel,
}: EntityDetailShellProps) {
  const runEdit = () => {
    onEdit?.();
    onOpenChange(false);
  };
  const editFooter = onEdit ? (
    <Button className="w-full" onClick={runEdit}>
      <Icon name="pencil" />
      {editLabel}
    </Button>
  ) : null;

  const resolvedFooter = onEdit ? editFooter : footer;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup size="xl">
        <DialogHeader>
          {header}
          <DialogDescription className="sr-only">
            {srDescription}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>{body}</DialogPanel>
        {resolvedFooter ? <DialogFooter>{resolvedFooter}</DialogFooter> : null}
      </DialogPopup>
    </Dialog>
  );
}

export function EntityDetailCard({ children }: { children: React.ReactNode }) {
  return <Card className="p-6">{children}</Card>;
}

export function EntityDetailHeader({
  entity,
  badge,
  padded,
}: Omittable<{
  entity: EntityData;
  badge?: React.ReactNode;
  padded?: boolean;
}>) {
  return (
    <Box direction="row" align="center" gap="md">
      <EntityTag
        entity={entity}
        display="full"
        size="lg"
        weight="semibold"
        interactive={false}
        padded={padded}
      />
      {badge}
    </Box>
  );
}
